import {useLingui} from "@lingui/react";
import {Trans} from "@lingui/react/macro";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import * as v from "valibot";
import {groupItemsByCategory, useSortedCategories} from "../../models/category-queries";
import createId, {IdType} from "../../models/create-id";
import type {
  GroupId,
  GroupRepo,
  IngredientEntry,
  ItemEntry,
  ItemId,
  RecipeEntry,
  RecipeId,
} from "../../models/GroupRepo";
import {getIncompleteVariants} from "../../models/item-queries";
import {useSortedIngredientSlices} from "../../models/recipe-queries";
import type {UnitList} from "../../models/unit-queries";
import {useUnitList} from "../../models/unit-queries";
import PlusMinusButtons from "../../shared/PlusMinusButtons";
import {tealTheme} from "../../styles/themes/color-themes.css";
import type {Nominal} from "../../types";
import {Col, css, Row, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import {prettyNumberInput} from "../../utils";
import {SelectField, TextField} from "../form/fields";
import {useForm} from "../form/Form";
import {quantitySchema} from "../form/form-utils";
import {ItemPillLayout, MiniPill, QuantityPill} from "../items/ItemPill";
import type {Potlist} from "./types";
import {clearPotlist, usePotlist} from "./usePotItems";

type PotlistEntry = [RecipeId, Potlist[RecipeId]];

const emptyPotList = {};

type MapEntry = {
  item: ItemEntry;
  variant: string | null;
  quantity: number;
  unit: string | null;
  recipeInfo: {recipe: RecipeEntry; portionCount: number}[];
  key: ItemVariantUnitKey;
};

type ItemVariantUnitKey = Nominal<string, "itemVariantUnitKey">;

const getKey = (opts: {itemId: ItemId; variant: string | null; unit: string | null}) => {
  return `${opts.itemId}:${opts.variant}:${opts.unit}` as ItemVariantUnitKey;
};

const useIngredientData = (groupId: GroupId, groupRepo: GroupRepo) => {
  const allRecipes = groupRepo.Recipes.useGetAll();
  const allIngredients = groupRepo.Ingredients.useGetAll();
  const allItems = groupRepo.Items.useGetAll();
  const potlist = usePotlist(groupId) ?? emptyPotList;
  const catsWithEntries = useSortedCategories(groupRepo, groupId);
  const {recipeInfoById, recipeIdSet} = useMemo(() => {
    const entries = Object.entries(potlist) as PotlistEntry[];
    const recipeInfoById = new Map<RecipeId, {recipe: RecipeEntry; portionCount: number}>();
    for (const [recipeId, {portionCount}] of entries) {
      const recipe = allRecipes[recipeId] as RecipeEntry | undefined;
      if (!recipe) continue;
      recipeInfoById.set(recipeId, {recipe, portionCount});
    }
    return {recipeInfoById, recipeIdSet: new Set(recipeInfoById.keys())};
  }, [allRecipes, potlist]);
  const ingredientSlices = useSortedIngredientSlices(groupRepo, recipeIdSet);
  return useMemo(() => {
    const itemByKey: Map<string, MapEntry> = new Map();
    const recipeInfoList: {recipe: RecipeEntry; portionCount: number}[] = [];
    for (const [recipeId, slice] of ingredientSlices) {
      const info = recipeInfoById.get(recipeId);
      if (!info) continue;
      const {recipe, portionCount} = info;
      recipeInfoList.push(info);
      for (const ingredientId of slice) {
        const ingredient = allIngredients[ingredientId] as IngredientEntry | undefined;
        if (!ingredient) continue;
        const item = allItems[ingredient.itemId] as ItemEntry | undefined;
        if (!item) continue;
        const variantKey = getKey({
          itemId: item.id,
          variant: ingredient.variant,
          unit: ingredient.unit,
        });
        const exist = itemByKey.get(variantKey);
        if (exist) {
          exist.recipeInfo.push({recipe, portionCount});
          exist.quantity += (ingredient.quantity * portionCount) / recipe.defaultPortions;
        } else {
          itemByKey.set(variantKey, {
            item,
            variant: ingredient.variant,
            quantity: (ingredient.quantity * portionCount) / recipe.defaultPortions,
            unit: ingredient.unit,
            recipeInfo: [{recipe, portionCount}],
            key: variantKey,
          });
        }
      }
    }
    const items = [...itemByKey.values()];
    const groups = groupItemsByCategory(
      items,
      (v) => v.item.categoryId,
      catsWithEntries.map(({cat}) => cat)
    );
    return {items, groups, recipeInfo: recipeInfoList};
  }, [allIngredients, allItems, catsWithEntries, ingredientSlices, recipeInfoById]);
};

const updateQuantityFormSchema = v.object({
  quantity: quantitySchema,
  unit: v.nullable(v.string()),
});

const ItemDetails = (props: UndoneItemTileProps) => {
  const {element, modification, resetModification, addModification, units} = props;
  const quantity = modification?.quantity ?? element.quantity;
  const unit = modification?.unit ?? element.unit;
  const {Form, field, setValue} = useForm({
    initial: {quantity: prettyNumberInput(quantity), unit},
    submitOnBlur: true,
    schema: updateQuantityFormSchema,
    onSubmit: ({unit, quantity}) => {
      addModification(element.key, quantity, unit);
    },
  });
  useEffect(() => {
    setValue("quantity", prettyNumberInput(quantity));
    setValue("unit", unit);
  }, [quantity, unit, setValue]);

  return (
    <Col rounded="4" borderWidth={1} borderColor="default" pa="16" sp="8">
      <Row sp="8">
        <Text bold>Recipe:</Text>
        <Row wrap sp="16">
          {element.recipeInfo.map(({recipe, portionCount}) => (
            <Row align="center" key={recipe.id} sp="4">
              <MiniPill>{portionCount}</MiniPill>
              <Text>{recipe.name}</Text>
            </Row>
          ))}
        </Row>
      </Row>
      <Form className={css({display: "flex", sp: "8"})}>
        <TextField
          field={field("quantity")}
          label={null}
          type="number"
          min="0"
          className={css({width: "100%"})}
        />
        <SelectField field={field("unit")} label={null} options={units} />
      </Form>
      <Row sp="8">
        <Button
          variant="primary"
          onClick={() => resetModification(element.key)}
          disabled={!modification}
        >
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={() => addModification(element.key, 0, unit)}
          disabled={quantity === 0}
        >
          Remove
        </Button>
      </Row>
    </Col>
  );
};

type UndoneItemTileProps = {
  element: MapEntry;
  modification: {quantity: number; unit: string | null} | undefined;
  addModification: (key: ItemVariantUnitKey, next: number, unit: string | null) => void;
  resetModification: (key: ItemVariantUnitKey) => void;
  isOpen: boolean;
  onClick: () => void;
  units: UnitList;
};
const ItemPill = (props: UndoneItemTileProps) => {
  const {element, addModification, modification, isOpen, onClick} = props;
  const handleUpdate = (next: number) => {
    addModification(element.key, next, element.unit);
  };
  const handleDelete = () => {
    addModification(element.key, 0, element.unit);
  };
  return (
    <>
      <ItemPillLayout
        emoji={element.item.emoji}
        theme={tealTheme}
        name={element.item.name}
        onClick={onClick}
      >
        <Row
          wrap
          sp="4"
          absolute
          align="end"
          justify="end"
          bottom="0"
          left="0"
          right="0"
          px="4"
          pb="4"
        >
          <QuantityPill variant={modification ?? element} />
          {element.variant && <MiniPill>{element.variant}</MiniPill>}
          <PlusMinusButtons
            value={modification?.quantity ?? element.quantity}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        </Row>
      </ItemPillLayout>
      {isOpen && <ItemDetails {...props} />}
    </>
  );
};

const ScheduleRecipes = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const group = groupRepo.vals.useValues();
  const {items, groups, recipeInfo} = useIngredientData(group.id, groupRepo);
  const [openItemKey, setOpenItemKey] = useState<ItemVariantUnitKey | null>(null);
  const navigate = useNavigate();

  const [modifications, setModifcations] = useState<
    Map<ItemVariantUnitKey, {quantity: number; unit: string | null}>
  >(new Map());
  const addModification = (key: ItemVariantUnitKey, next: number, unit: string | null) => {
    setModifcations((prev) => {
      const nextMap = new Map(prev);
      nextMap.set(key, {quantity: next, unit});
      return nextMap;
    });
  };
  const resetModification = (key: ItemVariantUnitKey) => {
    setModifcations((prev) => {
      const nextMap = new Map(prev);
      nextMap.delete(key);
      return nextMap;
    });
  };

  const handleItemClick = (key: ItemVariantUnitKey) => {
    setOpenItemKey(openItemKey === key ? null : key);
  };

  const handleSubmit = () => {
    navigate("..");
    const existingVariants = getIncompleteVariants(groupRepo);
    const varsByKey = new Map(existingVariants.map((v) => [getKey(v), v]));
    groupRepo.store.transaction(() => {
      for (const element of items) {
        const mod = modifications.get(element.key);
        const quantity = mod?.quantity ?? element.quantity;
        if (quantity === 0) continue;
        const unit = mod?.unit ?? element.unit;
        const existing = varsByKey.get(element.key);
        if (existing) {
          groupRepo.ItemVariants.patch(existing.id, {
            quantity: Number(prettyNumberInput(existing.quantity + quantity)),
          });
        } else {
          const variantId = createId(IdType.ItemVariant);
          groupRepo.ItemVariants.set(variantId, {
            id: variantId,
            itemId: element.item.id,
            variant: element.variant,
            quantity: Number(prettyNumberInput(quantity)),
            unit,
            completedAt: null,
          });
        }
      }
      const now = new Date();
      for (const {recipe, portionCount} of recipeInfo) {
        const schedId = createId(IdType.ScheduledRecipe);
        groupRepo.ScheduledRecipes.set(schedId, {
          id: schedId,
          recipeId: recipe.id,
          portionCount,
          scheduledAt: now,
          completedAt: null,
        });
      }
      clearPotlist(group.id);
    });
  };

  const {i18n} = useLingui();
  const units = useUnitList(groupRepo, i18n);

  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      {groups.length === 0 ? (
        <Text>
          No ingredients to add. Add recipes via <Icon name="pot-plus" inline />
        </Text>
      ) : (
        <Col sp="16">
          {groups.map(({category: cat, elements}) => (
            <Col sp="8" key={cat?.id ?? "null"}>
              {cat ? (
                <Text
                  size="16"
                  color="secondary"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  textWrap="nowrap"
                >
                  {cat.emoji} {cat.name}
                </Text>
              ) : (
                <Text>
                  <Trans id={"cat.other"}>other</Trans>
                </Text>
              )}
              <Col sp="4">
                {elements.map((element) => (
                  <ItemPill
                    key={element.key}
                    element={element}
                    modification={modifications.get(element.key)}
                    addModification={addModification}
                    resetModification={resetModification}
                    onClick={() => handleItemClick(element.key)}
                    isOpen={openItemKey === element.key}
                    units={units}
                  />
                ))}
              </Col>
            </Col>
          ))}
          <Row align="center" pt="8">
            <Button to="../potlist">Back</Button>
            <Button variant="primary" className={css({ml: "auto"})} onClick={handleSubmit}>
              Add Ingredients to List
            </Button>
          </Row>
        </Col>
      )}
    </Col>
  );
};

export default ScheduleRecipes;
